import { type ReactNode, Suspense } from 'react';

import { LoadingPage } from '@nesso/ui/blocks/loading-page';
import { Toaster } from '@nesso/ui/primitives/sonner';
import { TooltipProvider } from '@nesso/ui/primitives/tooltip';
import { ReactQueryDevtoolsToggle } from '@nesso/ui/providers/react-query-devtools-toggle';
import { ThemeProvider } from '@nesso/ui/providers/theme-provider';

import { ClientTimezoneProvider } from '@/app/providers/client-timezone-provider';
import { InactivitySignOutProvider } from '@/app/providers/inactivity-sign-out-provider';
import { InactivityTimerProvider } from '@/app/providers/inactivity-timer-provider';
import { QueryProvider } from '@/app/providers/query-provider';
import { WebSocketSubscriptions } from '@/app/providers/web-socket-subscriptions';
import { ConfirmDialog } from '@/shared/ui/dialogs/confirm-dialog';

type Props = {
    children: ReactNode;
};

export function AppProviders({ children }: Props) {
    return (
        <>
            <ClientTimezoneProvider />
            <ConfirmDialog />
            <TooltipProvider>
                <QueryProvider>
                    <InactivityTimerProvider>
                        <ThemeProvider
                            attribute="class"
                            defaultTheme="system"
                            disableTransitionOnChange
                        >
                            {/* Before children: sonner drops any toast published while it has
                                no subscriber, and a route can toast from a mount effect. */}
                            {/* A modal Radix layer sets pointer-events: none on body, and toasts render outside it. */}
                            <Toaster
                                toastOptions={{ classNames: { toast: 'pointer-events-auto' } }}
                            />
                            <Suspense fallback={<LoadingPage />}>
                                <InactivitySignOutProvider>{children}</InactivitySignOutProvider>
                            </Suspense>
                            <WebSocketSubscriptions />
                            <ReactQueryDevtoolsToggle appEnv={import.meta.env.VITE_APP_ENV} />
                        </ThemeProvider>
                    </InactivityTimerProvider>
                </QueryProvider>
            </TooltipProvider>
        </>
    );
}
